import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import GotoCartButton from '../../components/common/GotoCartButton';
import JacketGotoCartButton from '../../components/common/JacketGotoCartButton';
import SizeSelector from '../common/SizeSelector';
import {
  flattenPartsConfiguration,
  getFinalPriceForSelection,
  getNumberOfPartsConfigured,
  getSummaryInfo,
  getTshirtPrice
} from '../../containers/customizer.duck';
import { helmetViewMaps, tShirtBadgeMaps } from '../../utils/staticData';
import { availableHotspots } from '../../utils/tshirtHotspots';
import { useLocation } from 'react-router-dom';
// import { alertWrapper } from '../../utils/alertWrapper';

export const IS_ANDROID = (() => {
  return /android/i.test(navigator.userAgent);
})();

export const IS_IOS = (() => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
})();

const getInitMode = () => {
  if (IS_ANDROID) {
    return 'android';
  }
  if (IS_IOS) {
    return 'iOS';
  }
  return 'web';
};

const partialConflicts = {
  armour: [
    'JKT',
    'WNT',
    'RJC',
    'SWC',
    'EXC',
    'SRC',
    'WFC',
    'KDC',
    'NKC',
    'WSC'
  ],
  jacketColour: ['WNT', 'RJC', 'WSC'],
  winterProtection: [
    'RJC',
    'SWC',
    'EXC',
    'SRC',
    'WFC',
    'KDC',
    'NKC',
    'ERM',
    'BRM',
    'CRM',
    'JKT'
  ],
  winterColor: [
    'RJC',
    'SWC',
    'EXC',
    'SRC',
    'WFC',
    'KDC',
    'NKC',
    'ERM',
    'BRM',
    'CRM',
    'JKT'
  ],
  rainProtection: [
    'SWC',
    'EXC',
    'SRC',
    'WFC',
    'KDC',
    'NKC',
    'ERM',
    'BRM',
    'CRM',
    'JKT',
    'WSC',
    'WNT'
  ],
  rainProtectionNonActive: ['ERM', 'BRM', 'CRM', 'JKT', 'WSC', 'WNT']
};

const partialDeps = {
  armour: 'JKT',
  winterProtection: 'JKT',
  winterColor: 'JKT'
};

const ViewerWindow = ({
  partsConfig,
  setIsLoading,
  currentText,
  currentActiveParent,
  selectedCategory,
  activeHotspot,
  selectedVariant,
  selectedSize,
  variantData,
  setSelectedSize,
  siteUrl,
  // currentTextCustomization,
  currentCustomizations,
  setSnackbar,
  rotateTo,
  customizer,
  saveConfig,
  setActiveHotspotCode,
  savePartsConfiguration
}) => {
  const [sizeSelectorOpen, setSizeSelectorOpen] = useState(false);
  const [partialParts, setPartialParts] = useState([]);
  const location = useLocation();
  const isJacket = location.pathname.includes('jackets');
  const GoToCartButton = isJacket ? JacketGotoCartButton : GotoCartButton;

  const currentTextCustomization = currentCustomizations.text
    ? currentCustomizations.text.filter(
        entry =>
          entry.partId ===
          Object.keys(partsConfig.text).filter(
            key => partsConfig.text[key] === true
          )[0]
      )[0]
    : null;

  useEffect(() => {
    if (!window.ScapicSDK) {
      // not loaded already
      const sdkScript = document.createElement('script');
      sdkScript.src = process.env.REACT_APP_SDK_URL;
      document.head.appendChild(sdkScript);

      sdkScript.addEventListener('load', () => {
        const flatConfig = flattenPartsConfiguration(partsConfig, customizer);
        window.ScapicSDK.init(`@scapic/royalEnfield-${selectedVariant}`, {
          type: 'customizer',
          initMode: getInitMode(),
          loadConfig: { ...flatConfig },
          isFullScreenEnabled: true
        }).then(() => {
          // window.ScapicSDK.addEventListener('', hotspot => {
          //   setActiveHotspotCode(hotspot.substring(0, 2));
          //   // window.scapicSDK.triggerAnim(hotspot);
          // });
          if (!selectedVariant.includes('Helmet'))
            window.parent.postMessage(
              {
                module: selectedVariant === 'TShirt' ? 'tshirt' : 'jacket',
                event: 'sdk-loaded',
                configId: ''
              },
              siteUrl !== null ? siteUrl : '*'
            );
          if (window.ScapicSDK)
            window.ScapicSDK.analyticsDispatcher.addEventListener(
              'request_exit_fullscreen',
              data => {
                window.ScapicSDK.reposition(helmetViewMaps['default']);
              }
            );
          setIsLoading(false);
        });
      });
    } else {
      window.ScapicSDK.cleanup().then(() => {
        window.ScapicSDK.init(`@scapic/royalEnfield-${selectedVariant}`, {
          type: 'customizer',
          initMode: getInitMode(),
          isFullScreenEnabled: true
        }).then(() => {
          const flatConfig = flattenPartsConfiguration(
            partsConfig,
            customizer,
            currentText,
            currentTextCustomization
              ? currentTextCustomization.configData.hex
              : '#000'
          );
          window.ScapicSDK.update({
            configName: '<configName>',
            ...flatConfig
          });

          setIsLoading(false);
        });
      });
    }
  }, [selectedVariant]);

  useEffect(() => {
    let ignoredList = availableHotspots;
    let highlightList = [];
    if (currentActiveParent === 'front' && selectedCategory === 'frontBadge') {
      ignoredList = availableHotspots.filter(part => part.includes('S'));
      const activeHotspotCode = activeHotspot || 'TL';
      const activeBadge = Object.keys(partsConfig.frontBadge).find(
        key =>
          key.substring(0, 2) === activeHotspotCode &&
          partsConfig.frontBadge[key] === true
      );
      highlightList = activeBadge
        ? [activeBadge, `H${activeHotspotCode}001`]
        : [`H${activeHotspotCode}001`];
    } else if (selectedCategory === 'sleeve') {
      ignoredList = availableHotspots.filter(part => !part.includes('S'));
      const activeHotspotCode = activeHotspot || 'SL';
      const activeBadge = Object.keys(partsConfig.sleeve).find(
        key =>
          key.substring(0, 2) === activeHotspotCode &&
          partsConfig.sleeve[key] === true
      );
      highlightList = activeBadge
        ? [activeBadge, `H${activeHotspotCode}001`]
        : [`H${activeHotspotCode}001`];
    } else {
      ignoredList = availableHotspots;
      highlightList = [];
    }

    if (window.ScapicSDK) {
      const flatConfig = flattenPartsConfiguration(
        partsConfig,
        customizer,
        currentText,
        currentTextCustomization
          ? currentTextCustomization.configData.hex
          : '#000'
      );
      let activeRainJacket;
      if (partsConfig.rainProtection) {
        activeRainJacket = Object.keys(partsConfig.rainProtection).find(
          key => partsConfig.rainProtection[key] === true
        );
      }
      const isIntegratedWinterCoat =
        flatConfig.partVariation['WNT001'] === true ||
        flatConfig.partVariation['WNT002'] === true ||
        flatConfig.partVariation['WNT003'] === true ||
        flatConfig.partVariation['WNT004'] === true;

      if (
        currentActiveParent === 'armour' ||
        selectedCategory === 'jacketColour' ||
        selectedCategory === 'winterProtection' ||
        selectedCategory === 'winterColor' ||
        selectedCategory === 'rainProtection'
      ) {
        let validCategory =
          currentActiveParent === 'armour'
            ? currentActiveParent
            : selectedCategory;

        if (selectedCategory === 'rainProtection' && !activeRainJacket)
          validCategory = 'rainProtectionNonActive';

        const validConflicts =
          selectedCategory === 'winterProtection' && !isIntegratedWinterCoat
            ? ['RJC']
            : partialConflicts[validCategory];

        const selectedParts = Object.keys(flatConfig.partVariation).filter(
          part =>
            validConflicts.includes(part.substring(0, 3)) &&
            flatConfig.partVariation[part] === true
        );

        console.log(selectedParts, 'test');
        setPartialParts(selectedParts);
        //unselect
        selectedParts.forEach(part => {
          flatConfig.partVariation[part] = false;
          if (
            partialDeps[validCategory] &&
            part.includes(partialDeps[validCategory])
          ) {
            flatConfig.partVariation[
              partialDeps.armour + '1' + part.substring(4)
            ] = true;
          }
        });
      }

      window.ScapicSDK.update({
        configName: '<configName>',
        ...flatConfig,
        ignoreInteractions: ignoredList,
        highlight: highlightList
      });
      if (selectedCategory === 'frontBadge' || selectedCategory === 'sleeve') {
        const hotspot =
          activeHotspot || (selectedCategory === 'frontBadge' ? 'TL' : 'SL');
        window.ScapicSDK.reposition(tShirtBadgeMaps[hotspot]);
      }
    }
  }, [
    partsConfig,
    currentText,
    currentActiveParent,
    selectedCategory,
    activeHotspot
  ]);

  useEffect(() => {
    if (window.ScapicSDK && rotateTo !== 'none') {
      window.ScapicSDK.reposition(helmetViewMaps[rotateTo]);
    }
  }, [rotateTo, partsConfig, currentText]);

  useEffect(() => {
    if (variantData[selectedVariant]) {
      getFinalPriceForSelection(variantData[selectedVariant], partsConfig);
    }
  }, [variantData, partsConfig]);

  const numberOfParts = getNumberOfPartsConfigured(
    flattenPartsConfiguration(partsConfig, customizer).partVariation
  );
  const summaryData = getSummaryInfo(
    currentCustomizations,
    partsConfig,
    customizer
  );

  return (
    <>
      <SizeSelector
        isOpen={sizeSelectorOpen}
        selectedSize={selectedSize}
        setSnackbar={setSnackbar}
        selectedVariant={selectedVariant}
        onCloseButtonClick={() => {
          setSizeSelectorOpen(false);
        }}
        partsConfig={partsConfig}
        setSelectedSize={setSelectedSize}
        onAddToCartClick={() => {
          saveConfig();
          savePartsConfiguration(customizer);
          setSizeSelectorOpen(false);
        }}
      />
      <div className="viewer-window">
        <div
          className="viewer-window_sdk-container"
          data-scapic-id={`@scapic/royalEnfield-${selectedVariant}`}
        />
      </div>
      <div className="viewer-window_bottom-tab">
        <GoToCartButton
          views={numberOfParts}
          summaryData={summaryData}
          price={
            selectedVariant === 'TShirt'
              ? getTshirtPrice(partsConfig)
              : variantData[selectedVariant]
              ? `${getFinalPriceForSelection(
                  variantData[selectedVariant],
                  partsConfig
                )}`
              : `${0}`
          }
          onButtonClick={() => {
            setSizeSelectorOpen(true);
          }}
        />
      </div>
    </>
  );
};

ViewerWindow.propTypes = {
  partsConfig: PropTypes.object,
  selectedVariant: PropTypes.string,
  isDataReady: PropTypes.bool,
  variantData: PropTypes.object,
  setIsLoading: PropTypes.func,
  currentText: PropTypes.array,
  setSelectedSize: PropTypes.func,
  // currentTextCustomization: PropTypes.object,
  rotateTo: PropTypes.string,
  customizer: PropTypes.object,
  saveConfig: PropTypes.func,
  isConfigSaved: PropTypes.bool,
  openAlertDialog: PropTypes.func
};

export default ViewerWindow;
